import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from '../components/PublicNavBar';
import PortalFooter from '../components/PortalFooter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StarIcon from '@mui/icons-material/Star';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';

const STEPS = [
  {
    step: '01',
    title: 'Registrieren',
    description: 'Erstellen Sie Ihr kostenloses Profil und lassen Sie sich als Dienstleister freischalten.',
  },
  {
    step: '02',
    title: 'Gesuche durchsuchen',
    description: 'Durchsuchen Sie aktuelle Kundenanfragen nach Branche, Region, Budget und Umfang.',
  },
  {
    step: '03',
    title: 'Lead kaufen',
    description: 'Kaufen Sie den Kontakt für passende Leads und erhalten Sie alle Kundendaten direkt.',
  },
  {
    step: '04',
    title: 'Auftrag gewinnen',
    description: 'Kontaktieren Sie den Kunden und gewinnen Sie neue Aufträge für Ihr Call Center.',
  },
];

const BENEFITS = [
  {
    icon: <TrendingUpIcon />,
    title: 'Qualifizierte Leads',
    description: 'Alle Anfragen werden von unserem Team geprüft und freigegeben. Sie erhalten nur echte, konkrete Aufträge.',
  },
  {
    icon: <EuroIcon />,
    title: 'Transparente Preise',
    description: 'Kaufen Sie nur die Leads, die zu Ihnen passen. Kein Abo, keine versteckten Kosten – volle Kostenkontrolle.',
  },
  {
    icon: <GroupIcon />,
    title: 'Max. 3 Mitbewerber',
    description: 'Pro Lead werden maximal 3 Dienstleister kontaktiert – Ihre Chancen auf den Auftrag sind überdurchschnittlich hoch.',
  },
  {
    icon: <VerifiedUserIcon />,
    title: 'DSGVO-konform',
    description: 'Alle Kundendaten werden rechtskonform übermittelt. Vollständige Transparenz über Herkunft und Verwendung.',
  },
  {
    icon: <PhoneIcon />,
    title: 'Alle Branchen',
    description: 'Inbound, Outbound, Telesales, Kundensupport – finden Sie Aufträge, die exakt zu Ihren Stärken passen.',
  },
  {
    icon: <BusinessIcon />,
    title: 'Sofortiger Zugriff',
    description: 'Nach Freischaltung Ihres Profils erhalten Sie sofortigen Zugang zu allen aktuellen Gesuchen auf der Plattform.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Michael B.',
    role: 'Inhaber, Call Center',
    text: 'Als Dienstleister schätzen wir die Qualität der Leads. Die Kunden sind gut informiert und die Abschlussrate ist überdurchschnittlich hoch.',
    rating: 5,
  },
  {
    name: 'Julia R.',
    role: 'Vertrieb, Outbound-Center',
    text: 'Durch Callcenter-Suche haben wir unsere Auslastung deutlich steigern können. Die Leads sind gut qualifiziert und der Aufwand gering.',
    rating: 5,
  },
  {
    name: 'Klaus W.',
    role: 'Geschäftsführer, Service-Center',
    text: 'Einfache Bedienung, faire Preise, echte Aufträge. Genau das, was wir gesucht haben. Wir sind seit über einem Jahr dabei.',
    rating: 5,
  },
];

const DienstleisterPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <PublicNavBar />

      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 35%, #0f3460 65%, #533483 100%)',
          color: '#fff',
          py: { xs: 10, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-40%', right: '-20%',
            width: '70%', height: '180%',
            background: 'radial-gradient(ellipse, rgba(83,52,131,0.3) 0%, transparent 65%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-20%', left: '-10%',
            width: '60%', height: '120%',
            background: 'radial-gradient(ellipse, rgba(232,93,38,0.1) 0%, transparent 65%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="✓ Neue Aufträge für Ihr Call Center"
            sx={{
              mb: 4,
              backgroundColor: 'rgba(83,52,131,0.3)',
              color: '#c9b8f0',
              fontWeight: 700,
              border: '1px solid rgba(83,52,131,0.5)',
              fontSize: '0.8rem',
              letterSpacing: '0.02em',
            }}
          />
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 900,
              mb: 3,
              fontSize: { xs: '2.2rem', md: '3.2rem' },
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}
          >
            Mehr Aufträge für{' '}
            <Box component="span" sx={{ color: '#E85D26' }}>
              Ihr Call Center
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 6, opacity: 0.8, fontWeight: 400, maxWidth: 560, mx: 'auto', lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.1rem' } }}
          >
            Zugang zu qualifizierten Leads von Unternehmen, die aktiv einen Call-Center-Partner suchen – gezielt, transparent und ohne Streuverlust.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#E85D26',
                color: '#fff',
                fontWeight: 700,
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(232,93,38,0.5)',
                '&:hover': { backgroundColor: '#c94d1e', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(232,93,38,0.5)' },
                transition: 'all 0.2s ease',
              }}
              onClick={() => navigate('/register/contractor')}
            >
              Jetzt kostenlos registrieren
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'rgba(255,255,255,0.4)',
                color: '#fff',
                fontWeight: 600,
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 2,
                backdropFilter: 'blur(4px)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.8)', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
              onClick={() => navigate('/login')}
            >
              Bereits registriert? Anmelden
            </Button>
          </Stack>
          <Stack direction="row" spacing={4} justifyContent="center" sx={{ mt: 6, flexWrap: 'wrap', gap: 2 }}>
            {[
              { value: '1.500+', label: 'Aufträge vermittelt' },
              { value: '230+', label: 'Aktive Partner' },
              { value: 'Max. 3', label: 'Mitbewerber pro Lead' },
              { value: '98%', label: 'Zufriedenheit' },
            ].map((s) => (
              <Box key={s.label} sx={{ textAlign: 'center', px: 2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#E85D26', lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', opacity: 0.65, mt: 0.3 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: '#E85D26', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.75rem' }}
            >
              Ihre Vorteile als Partner
            </Typography>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 2, color: '#1a1a2e' }}>
              Warum Callcenter-Suche?
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
              Wir bringen Ihnen die Kunden – Sie konzentrieren sich auf das, was Sie am besten können.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {BENEFITS.map((b) => (
              <Grid key={b.title} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #f0f0f0',
                    p: 1,
                    borderRadius: 3,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: '#533483',
                      boxShadow: '0 12px 40px rgba(83,52,131,0.12)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: 'rgba(83,52,131,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                      }}
                    >
                      {React.cloneElement(b.icon as React.ReactElement<any>, { sx: { fontSize: 28, color: '#533483' } })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e' }}>
                      {b.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
                      {b.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: '#f8f9fa', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: '#E85D26', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.75rem' }}
            >
              In 4 Schritten zum Auftrag
            </Typography>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 2, color: '#1a1a2e' }}>
              So funktioniert's für Sie
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 540, mx: 'auto', lineHeight: 1.7 }}>
              Vom Profil zur ersten Anfrage in wenigen Minuten.
            </Typography>
          </Box>

          <Box
            sx={{
              background: 'linear-gradient(135deg, #fff 0%, #f5f5ff 100%)',
              borderRadius: 4,
              p: { xs: 3, md: 5 },
              border: '2px solid #e8e0f5',
              boxShadow: '0 4px 24px rgba(83,52,131,0.06)',
            }}
          >
            <Grid container spacing={3}>
              {STEPS.map((s) => (
                <Grid key={s.step} size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 2.5 }}>
                    <Box
                      sx={{
                        minWidth: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #533483 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 2px 8px rgba(83,52,131,0.35)',
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem' }}>
                        {s.step}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a2e' }}>
                        {s.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.65 }}>
                        {s.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 5, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #533483 0%, #764ba2 100%)',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 6,
                  py: 1.5,
                  boxShadow: '0 4px 16px rgba(83,52,131,0.4)',
                  '&:hover': { background: 'linear-gradient(135deg, #3d2563 0%, #533483 100%)', transform: 'translateY(-1px)' },
                }}
                onClick={() => navigate('/register/contractor')}
              >
                Jetzt als Dienstleister registrieren
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: '#E85D26', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.75rem' }}
            >
              Unsere Partner berichten
            </Typography>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 2, color: '#1a1a2e' }}>
              Was Dienstleister sagen
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {TESTIMONIALS.map((t) => (
              <Grid key={t.name} size={{ xs: 12, md: 4 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #f0f0f0',
                    borderRadius: 3,
                    p: 1,
                    transition: 'all 0.25s ease',
                    '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.08)', transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <StarIcon key={i} sx={{ color: '#FFB400', fontSize: 18 }} />
                      ))}
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ mb: 3, lineHeight: 1.75, color: '#444', fontStyle: 'italic', fontSize: '0.95rem' }}
                    >
                      „{t.text}"
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          background: 'linear-gradient(135deg, #533483 0%, #764ba2 100%)',
                          width: 40,
                          height: 40,
                          fontSize: '0.9rem',
                          fontWeight: 700,
                        }}
                      >
                        {t.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                          {t.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t.role}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #533483 100%)',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%', left: '-10%',
            width: '50%', height: '200%',
            background: 'radial-gradient(ellipse, rgba(83,52,131,0.2) 0%, transparent 65%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 900, color: '#fff', mb: 2, letterSpacing: '-0.02em' }}>
            Bereit für neue Aufträge?
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, fontWeight: 400, lineHeight: 1.6 }}>
            Registrieren Sie sich jetzt kostenlos und erhalten Sie sofortigen Zugang zu aktuellen Gesuchen.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#E85D26',
              color: '#fff',
              fontWeight: 700,
              px: 6,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(232,93,38,0.5)',
              '&:hover': { backgroundColor: '#c94d1e', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(232,93,38,0.5)' },
              transition: 'all 0.2s ease',
            }}
            onClick={() => navigate('/register/contractor')}
          >
            Jetzt kostenlos registrieren
          </Button>
          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 5, flexWrap: 'wrap', gap: 2 }}>
            {['Kostenlose Registrierung', 'Keine Mindestabnahme', 'Sofortiger Zugang'].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CheckCircleIcon sx={{ color: '#E85D26', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      <PortalFooter />
    </Box>
  );
};

export default DienstleisterPage;
